// login.rgseva.com — the organization's only sign-in screen.
//
// Lifecycle:
//   1. On load, silently POST /sso/token. A live icm_sso cookie → complete() at once
//      (popup signal / redirect to `next` / "you're signed in" card) — the user never
//      sees a form they don't need.
//   2. Otherwise render the form: GCIP email/password + one button per federated
//      provider. Either path yields a GCIP ID token, which POST /sso/login turns into
//      the org-wide SSO session. Then complete().
//
// The return-to-app rules (allow-list, popup protocol) live in lib/flow.ts.

import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Eye, EyeOff, Loader2, LogIn, LogOut } from "lucide-react";

import { completeSignIn, readFlowParams, type FlowParams } from "@/lib/flow";
import { GCIP_CONFIGURED, signInWithEmailPassword } from "@/lib/gcip";
import { ssoLogin, ssoLogout, ssoSilentSession, type SsoSession } from "@/lib/sso";
import { ProviderButtons } from "@/components/provider-buttons";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Login fields: 48px touch targets, 16px text (comfortable + iOS-zoom-safe).
const FIELD = "h-12 text-base border-muted-foreground/30";

type Stage =
  | "checking" // silent /sso/token probe in flight
  | "form" // no session — show sign-in UI
  | "redirecting" // location.replace(next) issued; this page is unloading
  | "popup-done" // opener signalled + window.close() attempted; fallback note
  | "signed-in"; // session exists but nowhere to send the user

export default function App() {
  const flow = useMemo<FlowParams>(() => readFlowParams(), []);
  const [stage, setStage] = useState<Stage>("checking");
  const [session, setSession] = useState<SsoSession | null>(null);
  // StrictMode mounts effects twice in dev; probe only once.
  const probed = useRef(false);

  function finish(s: SsoSession) {
    setSession(s);
    switch (completeSignIn(flow)) {
      case "popup-signalled":
        setStage("popup-done");
        break;
      case "redirected":
        setStage("redirecting");
        break;
      case "stay":
        setStage("signed-in");
        break;
    }
  }

  useEffect(() => {
    if (probed.current) return;
    probed.current = true;
    ssoSilentSession().then((s) => {
      if (s) finish(s);
      else setStage("form");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Both sign-in paths funnel here with a fresh GCIP ID token. */
  async function handleIdToken(idToken: string) {
    try {
      const s = await ssoLogin(idToken);
      toast.success("Signed in");
      finish(s);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Sign-in failed. Please try again.");
    }
  }

  async function handleSignOut() {
    await ssoLogout();
    setSession(null);
    toast.success("Signed out");
    setStage("form");
  }

  return (
    <div className="min-h-viewport pt-login-anchor mx-auto flex max-w-sm flex-col gap-6 p-6">
      {/* Brand header — the RGSEVA wordmark badge */}
      <div className="flex flex-col items-center gap-3 text-center">
        <div
          aria-hidden="true"
          className="grid size-16 place-items-center rounded-full border-4 border-orange-100 bg-gradient-to-b from-orange-500 to-orange-600 leading-none text-white shadow-lg shadow-orange-600/20"
        >
          <div className="text-[22px] font-bold tracking-tight">RG</div>
        </div>
        <div>
          <h1 className="text-xl font-bold">Sign in to RGSEVA</h1>
          <p className="text-sm text-muted-foreground">One account for all RGSEVA apps</p>
        </div>
      </div>

      {stage === "checking" || stage === "redirecting" ? (
        <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
          <p className="text-sm">{stage === "redirecting" ? "Taking you back…" : "Checking your session…"}</p>
        </div>
      ) : stage === "popup-done" ? (
        <StatusCard
          title="You're signed in"
          note="You can close this window — the app that opened it has picked up your session."
        />
      ) : stage === "signed-in" ? (
        <SignedInCard session={session} onSignOut={handleSignOut} />
      ) : (
        <Card className="bg-transparent py-0 shadow-none ring-0 sm:bg-card sm:py-6 sm:shadow-sm sm:ring-1">
          <CardContent className="px-0 pt-2 sm:px-6 sm:pt-0">
            {GCIP_CONFIGURED ? (
              <>
                <EmailPasswordForm onIdToken={handleIdToken} />
                <div className="mt-4 flex items-center gap-3" aria-hidden="true">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <div className="mt-4">
                  <ProviderButtons onIdToken={handleIdToken} />
                </div>
              </>
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Sign-in is not configured on this build (VITE_GCIP_API_KEY / VITE_GCIP_AUTH_DOMAIN
                are missing). Contact the administrator.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <p className="text-center text-xs text-muted-foreground">
        Signing in here signs you in to every RGSEVA app on this device.
      </p>
    </div>
  );
}

function StatusCard({ title, note }: { title: string; note: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-2 py-2 text-center">
        <CheckCircle2 className="size-8 text-emerald-600" />
        <p className="text-base font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{note}</p>
      </CardContent>
    </Card>
  );
}

function SignedInCard({
  session,
  onSignOut,
}: {
  session: SsoSession | null;
  onSignOut: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const who = session?.displayName || session?.email || "";

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-2 text-center">
        <CheckCircle2 className="size-8 text-emerald-600" />
        <div>
          <p className="text-base font-semibold">You're signed in to RGSEVA</p>
          {who && <p className="mt-1 text-sm text-muted-foreground">{who}</p>}
        </div>
        <p className="text-xs text-muted-foreground">
          Open any RGSEVA app and it will use this account automatically.
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-1 h-11 w-full"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            onSignOut().finally(() => setBusy(false));
          }}
        >
          {busy ? <Loader2 className="animate-spin" /> : <LogOut className="size-4" />} Sign out
        </Button>
      </CardContent>
    </Card>
  );
}

function EmailPasswordForm({ onIdToken }: { onIdToken: (idToken: string) => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const idToken = await signInWithEmailPassword(email.trim(), password);
      await onIdToken(idToken);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sign in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 pt-1">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          autoFocus
          placeholder="you@example.com"
          className={FIELD}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className={`${FIELD} pr-12`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute top-1/2 right-1 -translate-y-1/2 text-muted-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((s) => !s)}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </Button>
        </div>
      </div>
      <Button
        type="submit"
        className="mt-1 h-12 w-full text-base"
        disabled={busy || !email.trim() || !password}
      >
        {busy ? <Loader2 className="animate-spin" /> : <LogIn className="size-4" />}
        {busy ? "Signing in…" : "Sign in"}
      </Button>
    </form>
  );
}
